/**
 * @fileOverview The per-view walkthrough ("Erklärung") as a player. See docs/view-help.md.
 *
 * The walkthrough shows the **real product** in an iframe and plays through its regions one at a
 * time: the camera zooms onto a region, a pulsing dot and one short line of text appear, and after
 * a reading-length pause it moves on by itself — or at once, when the reader clicks the dot. A
 * timeline underneath shows every step, can be clicked to go back or ahead, and carries pause and
 * speed.
 *
 * This is the player's public face; the rest of this folder is its inside:
 *
 *   state.ts    — every piece of state, and `Move`, the token that lets the reader overtake a move
 *   tour.ts     — which steps this reader may see, and what the timeline derives from them
 *   moves.ts    — getting from one step to the next showable one, and landing on it
 *   frame.ts    — the frame's route, loading it, walking the pointer to a tab
 *   scenes.ts   — entering, catching up inside, and leaving a scene
 *   actions.ts  — what the pointer does: press, type, pick, scroll
 *   camera.ts, pointer.ts, input.ts — the commands the above give the stage and the frame
 *   clock.ts    — reading time, play and pause, speed
 *
 * `LewViewHelp` draws it; `services/viewHelp/` holds the content and the geometry.
 *
 * Module-level singleton, the same shape as `useOnboardingTour`. Nothing here touches the host
 * router: the reader's own page never changes.
 */

import router from "@/router"
import { logger } from "@/services/logger"
import { useOnboardingTour } from "@/composables/useOnboardingTour"
import { findTourForRoute, firstStepIndexForRoute } from "@/services/viewHelp/catalog"
import { visibleRegion } from "@/services/viewHelp/target"
import { prefersReducedMotion } from "./timing"
import {
  Move,
  cancelMoves,
  cameraMs,
  elapsed,
  endShown,
  focusCap,
  focusRect,
  frameLoadTarget,
  frameLoads,
  frameRoute,
  frameTrail,
  frameWindow,
  hold,
  isActive,
  isBusy,
  isFrameLoading,
  landed,
  playing,
  pointer,
  pointerCarry,
  pointerHolding,
  pointerKey,
  pointerPress,
  pointerPressButton,
  pointerVisible,
  skipped,
  speed,
  stepIndex,
  targetIndex,
  tourKey,
  worked,
} from "./state"
import {
  currentSection,
  displayIndex,
  dot,
  gatedSteps,
  hasHelpForRoute,
  isFinished,
  isRelatedSection,
  isLast,
  nextRoute,
  playable,
  progressOf,
  sections,
  step,
  steps,
  stepsInSection,
  targetOf,
  textOf,
  tour,
} from "./tour"
import { releaseHold } from "./camera"
import { forgetFrame, onFrameLoaded, reloadFrameOnto } from "./frame"
import { forgetScene } from "./scenes"
import { forgetPins } from "./actions"
import { forgetMoves, moveFrom, next, previous, seek, seekEdge, seekSection, showEnd, stepSection } from "./moves"
import { setHold, setPlaying, setSpeed, startClock, stepSpeed, stopClock, togglePlay } from "./clock"

export { SPEEDS } from "./timing"
export type { MoveMode } from "./moves"

export type ViewHelpCloseReason = "finished" | "dismissed" | "escape" | "lost"

function close(reason: ViewHelpCloseReason = "dismissed") {
  if (!isActive.value) return
  cancelMoves()
  stopClock()
  isActive.value = false
  isBusy.value = false
  isFrameLoading.value = false
  landed.value = false
  tourKey.value = null
  frameRoute.value = null
  frameLoadTarget.value = null
  frameWindow.value = null
  frameTrail.value = []
  worked.value = 0
  focusRect.value = null
  targetIndex.value = null
  focusCap.value = null
  endShown.value = false
  pointer.value = null
  pointerVisible.value = false
  pointerHolding.value = false
  pointerKey.value = null
  pointerCarry.value = null
  hold.value = false
  forgetFrame()
  forgetScene()
  forgetPins()
  forgetMoves()
  releaseHold()
  logger.log(`View help closed: ${reason}`)
}

/**
 * Opens the walkthrough on the view the reader is on — not at the tour's first step.
 *
 * Somebody pressing Erklärung on Prüfung is asking about Prüfung. The timeline still reaches the
 * views before it, so the whole chain stays available; only the starting point follows the reader.
 *
 * It plays by itself, except under `prefers-reduced-motion`, where it opens paused: auto-advancing
 * content is exactly what that preference asks to be spared, and the dot and the timeline still
 * move it on when the reader wants.
 */
async function start(): Promise<boolean> {
  if (isActive.value) return false
  // Never two modal overlays over the same page.
  if (useOnboardingTour().isActive.value) return false

  const routeName = router.currentRoute.value.name as string
  const candidate = findTourForRoute(routeName)
  if (!candidate) return false

  const available = gatedSteps(candidate)
  if (!available.length) return false

  tourKey.value = candidate.key
  skipped.value = new Set()
  endShown.value = false
  playing.value = !prefersReducedMotion()
  elapsed.value = 0
  worked.value = 0
  hold.value = false
  landed.value = false
  focusRect.value = null
  pointer.value = null
  pointerVisible.value = false
  isActive.value = true
  startClock()

  const openAt = firstStepIndexForRoute(available, routeName)
  stepIndex.value = openAt

  const opening = new Move()
  isBusy.value = true
  const loaded = await reloadFrameOnto(targetOf(available[openAt]))
  if (!opening.current) return isActive.value
  isBusy.value = false
  if (!loaded) {
    close("lost")
    return false
  }

  // The opening step goes the same way as any other — scene, actions, skipping and all — so the
  // first step can be one that does something, and one that has nothing to show simply passes.
  await moveFrom(openAt, 1, "step")
  return isActive.value
}

/** The reader clicked the dot or the text: move on, or on the last step, to the end screen. */
function advance() {
  if (isLast.value && !isBusy.value) showEnd()
  else void moveFrom(stepIndex.value + 1, 1, "step")
}

/** Re-measures the current region, for the stage's own resize handling. Never animates the pointer. */
function remeasure() {
  const win = frameWindow.value
  const current = step.value
  if (!win || !current?.anchor || !landed.value) return

  const rect = visibleRegion(win, current.anchor)
  if (rect) focusRect.value = rect
}

export function useViewHelp() {
  return {
    isActive,
    isBusy,
    isFrameLoading,
    landed,
    tour,
    steps,
    step,
    stepIndex,
    displayIndex,
    cameraMs,
    focusCap,
    playable,
    isLast,
    isFinished,
    endShown,
    sections,
    stepsInSection,
    currentSection,
    nextRoute,
    frameLoadTarget,
    frameLoads,
    frameTrail,
    isRelatedSection,
    focusRect,
    dot,
    pointer,
    pointerPress,
    pointerPressButton,
    pointerHolding,
    pointerKey,
    pointerCarry,
    pointerVisible,
    playing,
    speed,
    progressOf,
    textOf,
    hasHelpForRoute,
    onFrameLoaded,
    start,
    next,
    previous,
    advance,
    seek,
    seekSection,
    stepSection,
    seekEdge,
    togglePlay,
    setPlaying,
    setSpeed,
    stepSpeed,
    setHold,
    remeasure,
    close,
  }
}
